import React, { useState } from 'react';
import { fmt } from '../../../design/format';
import { ChartLegend, ChartTable, TipRow, TipTitle, allIntegers, nice, series, useChartTip, useChartWidth } from './core';

/**
 * Line / area chart (mockup VL.charts.line): crosshair tooltip, optional dashed target line,
 * dashed comparison series, end-point dot.
 * series: [{ name, values: (number|null)[], color?, slot?, dashed? }]
 */
export function LineChart({
  labels,
  series: lines,
  height = 220,
  ml = 40,
  yMin = 0,
  yMax: yMaxProp,
  yStep,
  yFmt = fmt.k,
  vFmt,
  target,
  area = false,
  legend = true,
  tipLabels,
  xName = 'Period',
  aria = 'Line chart',
  showTable = false,
}) {
  const [ref, W] = useChartWidth();
  const tip = useChartTip();
  const [hover, setHover] = useState(null);
  const valueFmt = vFmt || yFmt;
  const colorOf = (s, si) => s.color || series(s.slot || si + 1);

  if (showTable) {
    return (
      <div className="chart" ref={ref}>
        <ChartTable
          head={[xName, ...lines.map((s) => s.name)]}
          rows={labels.map((l, i) => [l, ...lines.map((s) => (s.values[i] == null ? '—' : valueFmt(s.values[i])))])}
        />
      </div>
    );
  }

  const H = height;
  const m = { l: ml, r: 14, t: 10, b: 26 };
  const all = [];
  lines.forEach((s) => s.values.forEach((v) => v != null && all.push(v)));
  if (target) all.push(target.value);
  const sc = nice(yMaxProp || Math.max(...all, 0), 4, allIntegers(all));
  const yMax = yMaxProp || sc.max;
  const step = yStep || sc.step;
  const n = labels.length;
  const pw = W - m.l - m.r;
  const ph = H - m.t - m.b;
  const x = (i) => m.l + (n === 1 ? pw / 2 : (i * pw) / (n - 1));
  const y = (v) => m.t + ph - ((v - yMin) / (yMax - yMin)) * ph;

  const ticks = [];
  for (let v = yMin; v <= yMax + 1e-9; v += step) ticks.push(v);

  const every = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(pw / 56))));
  const shown = [];
  for (let i = 0; i < n; i += every) shown.push(i);
  if (n && shown[shown.length - 1] !== n - 1) {
    if (x(n - 1) - x(shown[shown.length - 1]) < 52) shown.pop();
    shown.push(n - 1);
  }

  const onMove = (ev) => {
    const r = ev.currentTarget.ownerSVGElement.getBoundingClientRect();
    const px = ((ev.clientX - r.left) / r.width) * W;
    const i = Math.max(0, Math.min(n - 1, Math.round(((px - m.l) / pw) * (n - 1))));
    setHover(i);
    tip.show(
      <>
        <TipTitle>{tipLabels ? tipLabels[i] : labels[i]}</TipTitle>
        {lines.map((s, si) => (s.values[i] == null ? null : <TipRow key={s.name} color={colorOf(s, si)} label={s.name} value={valueFmt(s.values[i])} />))}
      </>,
      ev.clientX,
      ev.clientY
    );
  };

  return (
    <div className="chart" ref={ref}>
      {lines.length > 1 && legend && <ChartLegend items={lines.map((s, si) => ({ name: s.name, color: colorOf(s, si), line: true, dashed: s.dashed }))} />}
      <svg viewBox={`0 0 ${W} ${H}`} height={H} role="img" aria-label={aria}>
        {ticks.map((v) => (
          <React.Fragment key={v}>
            <line className="grid-line" x1={m.l} x2={W - m.r} y1={y(v)} y2={y(v)} />
            <text className="tick" x={m.l - 8} y={y(v) + 4} textAnchor="end">
              {yFmt(v)}
            </text>
          </React.Fragment>
        ))}
        {shown.map((i) => (
          <text key={i} className="tick" x={x(i)} y={H - 6} textAnchor={i === n - 1 && n > 1 ? 'end' : i === 0 ? 'start' : 'middle'}>
            {labels[i]}
          </text>
        ))}
        {target && (
          <>
            <line className="target-line" x1={m.l} x2={W - m.r} y1={y(target.value)} y2={y(target.value)} />
            <text className="lbl halo" x={W - m.r} y={y(target.value) - 6} textAnchor="end">
              {target.label}
            </text>
          </>
        )}
        {lines.map((s, si) => {
          const col = colorOf(s, si);
          const pts = s.values.map((v, i) => (v == null ? null : [x(i), y(v)])).filter(Boolean);
          if (!pts.length) return null;
          const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join('');
          const last = pts[pts.length - 1];
          return (
            <React.Fragment key={s.name}>
              {area && si === 0 && <path d={`${d}L${last[0]} ${y(yMin)}L${pts[0][0]} ${y(yMin)}Z`} style={{ fill: col, opacity: 0.1 }} />}
              <path
                d={d}
                style={{
                  fill: 'none',
                  stroke: col,
                  strokeWidth: 2,
                  strokeLinejoin: 'round',
                  strokeLinecap: 'round',
                  ...(s.dashed ? { strokeDasharray: '5 4', opacity: 0.75 } : {}),
                }}
              />
              {!s.dashed && <circle cx={last[0]} cy={last[1]} r={4} style={{ fill: col, stroke: 'var(--surface)', strokeWidth: 2 }} />}
            </React.Fragment>
          );
        })}
        <line className="crosshair" x1={hover == null ? 0 : x(hover)} x2={hover == null ? 0 : x(hover)} y1={m.t} y2={m.t + ph} visibility={hover == null ? 'hidden' : 'visible'} />
        <g>
          {hover != null &&
            lines.map((s, si) =>
              s.values[hover] == null ? null : (
                <circle key={s.name} cx={x(hover)} cy={y(s.values[hover])} r={4} style={{ fill: colorOf(s, si), stroke: 'var(--surface)', strokeWidth: 2 }} />
              )
            )}
        </g>
        <rect
          x={m.l}
          y={m.t}
          width={pw}
          height={ph}
          fill="transparent"
          onMouseMove={onMove}
          onMouseLeave={() => {
            setHover(null);
            tip.hide();
          }}
        />
      </svg>
      {tip.node}
    </div>
  );
}
